<template>
  <!-- 模块插入器组件 - 封装ContentInsertion和动态组件插入逻辑 -->
  <div class="module-inserter">
    <ContentInsertion
      ref="contentInsertion"
      :features="features"
      @insert-image="handleImageInsert"
      @insert-highlight="handleHighlightInsert"
      @insert-columns="handleColumnsInsert"
      @insert-table="handleTableInsert"
      @insert-file="handleFileInsert"
    />
  </div>
</template>

<script>
/**
 * ModuleInserter 模块插入器组件
 * 
 * 功能说明：
 * - 封装 ContentInsertion 组件及动态组件插入逻辑
 * - 支持图片、高亮块、分栏布局、表格、文件的插入
 * - 动态创建 Vue 组件实例并插入到 contenteditable 元素内部
 * - 自动管理组件生命周期（创建、挂载、销毁）
 * 
 * 使用示例：
 * ```vue
 * <ModuleInserter
 *   ref="moduleInserter"
 *   :features="['image', 'highlight', 'columns', 'table', 'file']"
 *   :target-ref="targetRef"
 *   @image-upload="handleImageUpload"
 * />
 * ```
 * 
 * API 说明：
 * Props:
 * - features: Array, 可用功能列表，默认 ['image', 'highlight', 'columns', 'table', 'file']
 * - targetRef: Object, 目标 contenteditable 元素的 ref（用于插入组件的位置）
 * 
 * Events:
 * - image-upload(): 触发图片上传请求，父组件需处理上传逻辑
 * 
 * Methods:
 * - openPanel(): 打开插入面板
 * - destroyComponents(): 销毁所有动态创建的组件
 */

import ContentInsertion from './ContentInsertion.vue'
import Id10 from './id_10.vue'
import Id11 from './id_11.vue'
import Id12 from './id_12.vue'
import Id13 from './id_13.vue'
import Vue from 'vue'

export default {
  name: 'ModuleInserter',
  
  components: {
    ContentInsertion
  },
  
  props: {
    /**
     * 可用功能列表
     * @type {Array}
     * @default ['image', 'highlight', 'columns', 'table', 'file']
     */
    features: {
      type: Array,
      default: () => ['image', 'highlight', 'columns', 'table', 'file']
    },
    
    /**
     * 目标 contenteditable 元素的 ref
     * 用于定位组件插入位置
     * @type {HTMLElement}
     * @default null
     */
    targetRef: {
      type: HTMLElement,
      default: null
    }
  },
  
  data () {
    return {
      // 动态创建的组件实例列表（用于销毁管理）
      mountedComponents: [],
      // 组件ID计数器
      componentIdCounter: 0,
      // 组件类型映射表
      componentMap: {
        highlight: Id13,
        columns: Id11,
        table: Id12,
        file: Id10
      }
    }
  },
  
  methods: {
    /**
     * 打开插入功能面板
     */
    openPanel () {
      this.$refs.contentInsertion.openPanel()
    },
    
    /**
     * 处理图片插入事件
     * 触发 image-upload 事件由父组件处理上传逻辑
     */
    handleImageInsert () {
      this.$emit('image-upload')
    },
    
    /**
     * 处理高亮块插入事件
     * @param {Object} defaultData - 组件默认数据
     */
    handleHighlightInsert (defaultData) {
      this.insertVueComponent('highlight', defaultData)
    },
    
    /**
     * 处理分栏布局插入事件
     * @param {Object} defaultData - 组件默认数据
     */
    handleColumnsInsert (defaultData) {
      this.insertVueComponent('columns', defaultData)
    },
    
    /**
     * 处理表格插入事件
     * @param {Object} defaultData - 组件默认数据
     */
    handleTableInsert (defaultData) {
      this.insertVueComponent('table', defaultData)
    },
    
    /**
     * 处理文件插入事件
     * @param {Object} defaultData - 组件默认数据
     */
    handleFileInsert (defaultData) {
      this.insertVueComponent('file', defaultData)
    },
    
    /**
     * 动态创建 Vue 组件实例并插入到 contenteditable 内部
     * @param {String} type - 组件类型标识
     * @param {Object} data - 组件 props 数据
     */
    insertVueComponent (type, data) {
      // 根据类型获取组件
      const Component = this.componentMap[type]
      if (!Component) {
        console.error(`未支持的组件类型: ${type}`)
        return
      }
      
      // 获取目标元素
      const targetEl = this.targetRef
      if (!targetEl) {
        console.error('未提供目标元素 ref')
        return
      }
      
      this.componentIdCounter++
      
      // 创建组件构造器
      const ComponentConstructor = Vue.extend(Component)
      
      // 创建组件实例
      const propsData = {
        data: data
      }
      // 如果是高亮块组件，设置embedded为true
      if (type === 'highlight') {
        propsData.embedded = true
      }
      
      const componentInstance = new ComponentConstructor({
        propsData: propsData
      })
      
      // 将组件挂载到一个临时元素
      const tempContainer = document.createElement('div')
      tempContainer.setAttribute('contenteditable', 'false')
      componentInstance.$mount(tempContainer)
      
      // 获取挂载后的 DOM 节点
      const componentEl = componentInstance.$el
      
      if (componentEl) {
        // 将组件 DOM 节点插入到 contenteditable 内部
        targetEl.appendChild(componentEl)
        
        // 在组件之后插入一个空的 <p> 标签，用于后续文本输入
        const emptyParagraph = document.createElement('p')
        emptyParagraph.className = 'empty-text-paragraph'
        emptyParagraph.innerHTML = '<br>'
        targetEl.insertBefore(emptyParagraph, componentEl.nextSibling)
        
        // 保存组件实例引用用于销毁管理
        this.mountedComponents.push({
          id: this.componentIdCounter,
          instance: componentInstance
        })
        
        // 更新高度（通知父组件）
        this.$emit('component-inserted')
      } else {
        console.error('无法插入组件：组件实例创建失败')
        componentInstance.$destroy()
      }
    },
    
    /**
     * 在指定位置动态创建 Vue 组件实例并替换占位符（首次渲染场景）
     * @param {String} type - 组件类型标识
     * @param {Object} data - 组件 props 数据
     * @param {HTMLElement} placeholder - 占位符元素，将被组件替换
     */
    insertVueComponentAt (type, data, placeholder) {
      // 根据类型获取组件
      const Component = this.componentMap[type]
      if (!Component) {
        console.error(`未支持的组件类型: ${type}`)
        return
      }
      
      if (!placeholder || !placeholder.parentNode) {
        console.error('无效的占位符元素')
        return
      }
      
      this.componentIdCounter++
      
      // 创建组件构造器
      const ComponentConstructor = Vue.extend(Component)
      
      // 创建组件实例
      const propsData = {
        data: data
      }
      // 如果是高亮块组件，设置embedded为true
      if (type === 'highlight') {
        propsData.embedded = true
      }
      
      const componentInstance = new ComponentConstructor({
        propsData: propsData
      })
      
      // 将组件挂载到一个临时元素
      const tempContainer = document.createElement('div')
      tempContainer.setAttribute('contenteditable', 'false')
      componentInstance.$mount(tempContainer)
      
      // 获取挂载后的 DOM 节点
      const componentEl = componentInstance.$el
      
      if (componentEl) {
        // 在组件根元素上设置 contenteditable="false"，防止浏览器 contenteditable 实现
        // 对组件内部 DOM 结构进行错误的重组或移动
        componentEl.setAttribute('contenteditable', 'false')
        
        // 用组件替换占位符（首次渲染，不需要插入空p标签）
        placeholder.parentNode.replaceChild(componentEl, placeholder)
        
        // 保存组件实例引用用于销毁管理
        this.mountedComponents.push({
          id: this.componentIdCounter,
          instance: componentInstance
        })
        
        // 更新高度（通知父组件）
        this.$emit('component-inserted')
      } else {
        console.error('无法插入组件：组件实例创建失败')
        componentInstance.$destroy()
      }
    },
    
    /**
     * 销毁所有动态创建的组件
     */
    destroyComponents () {
      this.mountedComponents.forEach(item => {
        if (item.instance) {
          const componentEl = item.instance.$el
          
          // 移除组件后面紧跟的空段落
          if (componentEl && componentEl.nextSibling &&
              componentEl.nextSibling.classList &&
              componentEl.nextSibling.classList.contains('empty-text-paragraph')) {
            componentEl.nextSibling.remove()
          }
          
          // 销毁组件实例
          item.instance.$destroy()
          
          // 移除组件 DOM 节点
          if (componentEl && componentEl.parentNode) {
            componentEl.parentNode.removeChild(componentEl)
          }
        }
      })
      this.mountedComponents = []
      this.componentIdCounter = 0
    },
    
    /**
     * 获取已插入的组件数量
     * @returns {Number}
     */
    getComponentCount () {
      return this.mountedComponents.length
    },
    
    /**
     * 获取所有已插入组件的DOM和实例信息
     * @returns {Array} 包含 { id, dom, instance } 的数组
     */
    getInsertedComponentsData () {
      const result = []
      this.mountedComponents.forEach(item => {
        if (item.instance && item.instance.$el) {
          result.push({
            instanceId: item.id,
            dom: item.instance.$el,
            componentInstance: item.instance
          })
        }
      })
      return result
    },
    
    /**
     * 删除单个已插入的组件
     * @param {Number} instanceId - 组件实例ID
     */
    removeComponentById (instanceId) {
      const index = this.mountedComponents.findIndex(item => item.id === instanceId)
      if (index !== -1) {
        const item = this.mountedComponents[index]
        if (item.instance) {
          const componentEl = item.instance.$el
          
          // 移除组件后面紧跟的空段落
          if (componentEl && componentEl.nextSibling &&
              componentEl.nextSibling.classList &&
              componentEl.nextSibling.classList.contains('empty-text-paragraph')) {
            componentEl.nextSibling.remove()
          }
          
          // 销毁组件实例
          item.instance.$destroy()
          
          // 移除组件 DOM 节点
          if (componentEl && componentEl.parentNode) {
            componentEl.parentNode.removeChild(componentEl)
          }
        }
        this.mountedComponents.splice(index, 1)
      }
    },
    
    /**
     * 根据DOM元素删除组件
     * @param {HTMLElement} dom - 组件的DOM元素
     */
    removeComponentByDom (dom) {
      const index = this.mountedComponents.findIndex(item => item.instance && item.instance.$el === dom)
      if (index !== -1) {
        const item = this.mountedComponents[index]
        if (item.instance) {
          // 销毁组件实例
          item.instance.$destroy()
        }
        this.mountedComponents.splice(index, 1)
      }
    },
    
    /**
     * 更新目标元素引用
     * @param {HTMLElement} ref - 新的目标元素
     */
    setTargetRef (ref) {
      this.targetRef = ref
      this.$emit('update:targetRef', ref)
    }
  },
  
  beforeDestroy () {
    // 组件销毁前清理所有动态创建的组件
    this.destroyComponents()
  }
}
</script>

<style lang="scss" scoped>
.module-inserter {
  display: inline-block;
}
</style>



// WEBPACK FOOTER //
// src/components/module/course/public/ModuleInserter.vue